import createCachedSelector from 're-reselect'
import { createSelector } from 'reselect'
import { selectChannelCustomFieldsCategoriesById } from 'ducks/crm/channels/selectors/selectChannelCustomFieldsCategoriesById'
import { emptyArr } from 'util/arrays'
import { createBasicSelector } from 'util/redux'

import { selectCustomFields, selectCustomFieldsById } from './base'
import { sortCustomFieldsForSettingsDisplay } from './utils'

export const selectCustomFieldsByCategoryId = createSelector(
  selectCustomFieldsById,
  selectChannelCustomFieldsCategoriesById,
  (fields, channelCategoriesById) => {
    const rep = Object.assign(
      Object.values(fields).reduce((result, field) => {
        const { category } = field

        if (!result[category]) {
          // eslint-disable-next-line no-param-reassign
          result[category] = []
        }

        if (!field.deletedAt) {
          result[category].push(field)
        }

        return result
      }, {}),
      channelCategoriesById
    )
    return rep
  }
)

export const selectCustomFieldsForCategoryId = createBasicSelector(
  selectCustomFieldsByCategoryId,
  (state, { categoryId }) => categoryId,
  (state, { categoryKey }) => categoryKey,
  (state, { forDisplay }) => forDisplay,
  (fieldByCategoryId, categoryId, categoryKey, forDisplay) => {
    // First try to find by categoryId
    let fields = fieldByCategoryId[categoryId]

    // If not found and categoryKey is provided, try looking up by key
    // This handles cases where entity store categories have different ids
    // but same keys as channel categories
    if (!fields && categoryKey && fieldByCategoryId[categoryKey]) {
      fields = fieldByCategoryId[categoryKey]
    }

    fields = fields || emptyArr

    if (forDisplay) {
      return sortCustomFieldsForSettingsDisplay(fields)
    }

    return fields
  }
)

export const selectTrashedCustomFieldsForCategoryId = createCachedSelector(
  selectCustomFields,
  (state, { categoryId }) => categoryId,
  (fields, categoryId) => {
    return fields.filter(
      ({ deletedAt, category }) => !!deletedAt && category === categoryId
    )
  }
)((state, { categoryId }) => categoryId)

export const selectCustomFieldsForCategoryIdByKey = createCachedSelector(
  selectCustomFieldsForCategoryId,
  fields => {
    return fields.reduce((result, field) => {
      // eslint-disable-next-line no-param-reassign
      result[field.key] = field
      return result
    }, {})
  }
)((state, { categoryId, categoryKey }) => `${categoryId}-${categoryKey || ''}`)

export const selectCustomFieldIdsForCategoryId = createCachedSelector(
  selectCustomFieldsByCategoryId,
  (_state, { categoryId }) => categoryId,
  (_state, { categoryKey }) => categoryKey,
  (customFieldsByCategoryId, categoryId, categoryKey) => {
    // First try to find by categoryId
    const fieldsById = customFieldsByCategoryId[categoryId]
    if (fieldsById) {
      return fieldsById.map(({ id }) => id)
    }
    // If not found and categoryKey is provided, try looking up by key
    // This handles cases where entity store categories have different ids
    // but same keys as channel categories
    if (categoryKey && customFieldsByCategoryId[categoryKey]) {
      return customFieldsByCategoryId[categoryKey].map(({ id }) => id)
    }
    return emptyArr
  }
)((state, { categoryId, categoryKey }) => `${categoryId}-${categoryKey || ''}`)
